import { IconProp } from "@fortawesome/fontawesome-svg-core"
import {
  faDotCircle,
  faDownload,
  faEdit,
  faEye,
  faPaperPlane,
  faTrashCan
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, IconButton, Tooltip, useTheme } from "@mui/material"
import { FC } from "react"
import {
  CustomTableButtonProps,
  TableButtonProps,
  TableButtonTypeEnum
} from "./tableButtonProps.interface"
import { useTranslation } from "react-i18next"

export interface buttonStyleProps {
  label?: string
  icon: IconProp
  color:
  | "inherit"
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning"
}

const TableButton: FC<TableButtonProps & { toolTipText?: string }> = ({
  type,
  iconSize,
  custom,
  handleOnClick,
  toolTipText,
  disabled
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { icon, label, color } = custom
    ? custom
    : ({ icon: faDotCircle, label: "", color: "secondary" } as CustomTableButtonProps)

  const wrapTooltip = (node: JSX.Element) =>
    toolTipText ? (
      <Tooltip title={toolTipText}>
        {disabled ? <span style={{ display: "inline-flex" }}>{node}</span> : node}
      </Tooltip>
    ) : node

  switch (type) {
    case TableButtonTypeEnum.Edit:
      return (
        <Box justifyContent="flex-end" display="flex">
          {wrapTooltip(
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                handleOnClick()
              }}
              sx={{ display: { xs: "none", md: "flex" } }}
              size="small"
              color={"secondary"}
              disabled={disabled}>
              <FontAwesomeIcon icon={faEdit} />
            </IconButton>
          )}
          <Button
            onClick={(event) => {
              event.stopPropagation()
              handleOnClick()
            }}
            sx={{ display: { xs: "flex", md: "none" } }}
            size="small"
            color={"primary"}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faEdit} />}
            disabled={disabled}>
            {t("global.edit", "Edit")}
          </Button>
        </Box>
      )

    case TableButtonTypeEnum.View:
      return (
        <Box justifyContent="flex-end" display="flex">
          {wrapTooltip(
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                handleOnClick()
              }}
              sx={{ display: { xs: "none", md: "flex" } }}
              size="small"
              color={"secondary"}
              disabled={disabled}>
              <FontAwesomeIcon icon={faEye} />
            </IconButton>
          )}
          <Button
            onClick={(event) => {
              event.stopPropagation()
              handleOnClick()
            }}
            sx={{ display: { xs: "flex", md: "none" } }}
            size="small"
            color={"primary"}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faEye} />}
            disabled={disabled}>
            {t("global.view", "View")}
          </Button>
        </Box>
      )

    case TableButtonTypeEnum.Delete:
      return (
        <Box justifyContent="flex-end" display="flex">
          {wrapTooltip(
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                handleOnClick()
              }}
              sx={{ display: { xs: "none", md: "flex" } }}
              size="small"
              color={"secondary"}
              disabled={disabled}>
              <FontAwesomeIcon icon={faTrashCan} />
            </IconButton>
          )}
          <Button
            onClick={(event) => {
              event.stopPropagation()
              handleOnClick()
            }}
            sx={{ display: { xs: "flex", md: "none" } }}
            size="small"
            color={"primary"}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faTrashCan} />}
            disabled={disabled}>
            {t("global.delete", "Delete")}
          </Button>
        </Box>
      )

    case TableButtonTypeEnum.Download:
      return (
        <Box justifyContent="flex-end" display="flex">
          {wrapTooltip(
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                handleOnClick()
              }}
              sx={{ display: { xs: "none", md: "flex" } }}
              size="small"
              color={"secondary"}
              disabled={disabled}>
              <FontAwesomeIcon icon={faDownload} />
            </IconButton>
          )}
          <Button
            onClick={(event) => {
              event.stopPropagation()
              handleOnClick()
            }}
            sx={{ display: { xs: "flex", md: "none" } }}
            size="small"
            color={"primary"}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faDownload} />}
            disabled={disabled}>
            {t("global.download", "Download")}
          </Button>
        </Box>
      )

    case TableButtonTypeEnum.Send:
      return (
        <Box justifyContent="flex-end" display="flex">
          {wrapTooltip(
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                handleOnClick()
              }}
              sx={{ display: { xs: "none", md: "flex" } }}
              size="small"
              color={"secondary"}
              disabled={disabled}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </IconButton>
          )}
          <Button
            onClick={(event) => {
              event.stopPropagation()
              handleOnClick()
            }}
            sx={{ display: { xs: "flex", md: "none" } }}
            size="small"
            color={"primary"}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
            disabled={disabled}>
            {t("global.send", "Send")}
          </Button>
        </Box>
      )

    case TableButtonTypeEnum.Custom:
      return (
        <Box justifyContent="flex-end" display="flex">
          {wrapTooltip(
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                handleOnClick()
              }}
              sx={{ display: { xs: "none", md: "flex" } }}
              size="small"
              color={color ? color : "secondary"}
              disabled={disabled}>
              <FontAwesomeIcon icon={icon} />
            </IconButton>
          )}
          <Button
            onClick={(event) => {
              event.stopPropagation()
              handleOnClick()
            }}
            sx={{ display: { xs: "flex", md: "none" } }}
            size="small"
            color={color ? color : "primary"}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={icon} />}
            disabled={disabled}>
            {label}
          </Button>
        </Box>
      )

    default:
      return (
        <Box justifyContent="flex-end" display="flex">
          {wrapTooltip(
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                handleOnClick()
              }}
              sx={{ display: { xs: "none", md: "flex" } }}
              size="small"
              color={"secondary"}
              disabled={disabled}>
              <FontAwesomeIcon icon={faEdit} />
            </IconButton>
          )}
          <Button
            onClick={(event) => {
              event.stopPropagation()
              handleOnClick()
            }}
            sx={{ display: { xs: "flex", md: "none" } }}
            size="small"
            color={"primary"}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faEdit} />}
            disabled={disabled}>
            {t("global.edit", "Edit")}
          </Button>
        </Box>
      )
  }
}

export default TableButton
